import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Lock, AlertCircle, CheckCircle2, User, MapPin, Calendar, DollarSign } from 'lucide-react';
import { formatBookingReference, formatPaymentMethod } from '../../../utils/bookingUtils';
import { formatTimeRange } from '../BookingSeries/seriesHelpers';
import { bookingStatusBadge } from './badges';

export default function BookingCard({ booking, onReleaseClick }) {
  const { release_eligibility: eligibility } = booking;
  const customerName = booking.customer_name || 'N/A';
  const courts = (booking.courts_assigned || booking.courts_requested || [])
    .map((c) => (c.court_number ? `Court ${c.court_number}` : c.sport))
    .join(', ') || 'N/A';

  return (
    <Card className="p-5" data-testid="release-booking-card">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-4">
        <div>
          <div className="font-mono text-sm text-muted-foreground" data-testid="release-booking-ref">
            {formatBookingReference(booking.id)}
          </div>
          <div className="text-lg font-bold mt-0.5">{customerName}</div>
        </div>
        {bookingStatusBadge(booking.status)}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
          <span>{courts}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
          <span>{booking.booking_date} · {formatTimeRange(booking.time_slot, booking.duration)}</span>
        </div>
        <div className="flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-muted-foreground shrink-0" />
          <span>
            ${(booking.final_total || 0).toFixed(2)} paid via {formatPaymentMethod(booking.payment_method)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <User className="w-4 h-4 text-muted-foreground shrink-0" />
          <span>Payment status: <strong>{booking.payment_status || 'N/A'}</strong></span>
        </div>
      </div>

      {eligibility?.locked && (
        <div
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700"
          data-testid="release-locked-state"
        >
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{eligibility.reason}</span>
        </div>
      )}

      {!eligibility?.locked && !eligibility?.eligible && (
        <div
          className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-800"
          data-testid="release-ineligible-state"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{eligibility.reason}</span>
        </div>
      )}

      {eligibility?.eligible && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            Eligible for release
          </div>
          <Button
            variant="destructive"
            size="sm"
            onClick={onReleaseClick}
            data-testid="release-booking-button"
          >
            Release Booking
          </Button>
        </div>
      )}
    </Card>
  );
}
